import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Sparkles, Lock, Mail, ArrowRight, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { InputOTP, InputOTPGroup, InputOTPSlot, InputOTPSeparator } from "@/components/ui/input-otp";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { user, login, completeMfa } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mfaToken, setMfaToken] = useState(null);
  const [otp, setOtp] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);

  if (user) return <Navigate to={user.role === "super_admin" ? "/admin" : "/"} replace />;

  const handleLogin = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    setError("");
    try {
      const res = await login(email, password);
      if (res.mfaRequired) {
        setMfaToken(res.mfaToken);
        toast.info("Masukkan kode 2FA dari aplikasi authenticator Anda");
      } else {
        toast.success("Selamat datang kembali!");
        navigate(res.user?.role === "super_admin" ? "/admin" : "/");
      }
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  const handleMfa = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    setError("");
    try {
      const u = await completeMfa(mfaToken, useBackup ? { backupCode } : { code: otp });
      toast.success("Verifikasi berhasil!");
      navigate(u?.role === "super_admin" ? "/admin" : "/");
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Kode 2FA salah");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left hero panel */}
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1595871151608-bc7abd1caca3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBiZWF1dHklMjBjbGluaWMlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODE1NDEyMjl8MA&ixlib=rb-4.1.0&q=85"
          alt="Beauty clinic interior"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-foreground/60 via-foreground/20 to-transparent" />
        <div className="relative z-10 flex h-full flex-col justify-between p-12 text-background">
          <div className="flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-background text-foreground">
              <Sparkles className="h-4 w-4" />
            </div>
            <span className="font-heading text-lg">MyClinic</span>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] opacity-80">
              Beauty Clinic OS
            </div>
            <h1 className="font-heading text-4xl leading-tight tracking-tight mt-4 max-w-md">
              Naik level ke operasional clinic kecantikan yang profesional.
            </h1>
            <p className="mt-4 max-w-md text-sm opacity-80">
              Booking realtime, ekosistem database pasien tersegmentasi, dashboard
              sales & inisiatif yang akurat — semua dalam satu workspace.
            </p>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-6 flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-lg bg-foreground text-background">
              <Sparkles className="h-4 w-4" />
            </div>
            <span className="font-heading text-lg">MyClinic</span>
          </div>

          <h2 className="font-heading text-2xl tracking-tight">Masuk ke MyClinic</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Kelola booking, sales, dan ekosistem pasien Anda.
          </p>

          {mfaToken ? (
            <form className="mt-8 space-y-4" onSubmit={handleMfa} data-testid="mfa-form">
              <div className="rounded-md bg-secondary/50 p-3 text-sm text-muted-foreground flex items-center gap-2">
                <ShieldCheck className="h-4 w-4" /> Verifikasi dua faktor diperlukan untuk akun ini.
              </div>
              {!useBackup ? (
                <div>
                  <Label>Kode Authenticator (6 digit)</Label>
                  <div className="mt-2 flex justify-center">
                    <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="mfa-otp-input">
                      <InputOTPGroup>
                        <InputOTPSlot index={0} /><InputOTPSlot index={1} /><InputOTPSlot index={2} />
                      </InputOTPGroup>
                      <InputOTPSeparator />
                      <InputOTPGroup>
                        <InputOTPSlot index={3} /><InputOTPSlot index={4} /><InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  </div>
                </div>
              ) : (
                <div>
                  <Label htmlFor="backup">Backup Code</Label>
                  <div className="relative mt-1.5">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input id="backup" data-testid="mfa-backup-input" value={backupCode} onChange={(e) => setBackupCode(e.target.value.toUpperCase())} className="pl-9 font-mono" placeholder="XXXXXXXXXX" />
                  </div>
                </div>
              )}
              {error && <div data-testid="login-error" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}
              <Button type="submit" data-testid="mfa-submit-btn" disabled={loading} className="w-full">
                {loading ? "Memverifikasi..." : "Verifikasi"}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <button type="button" className="text-xs text-muted-foreground hover:text-foreground" data-testid="mfa-toggle-backup" onClick={() => { setUseBackup(!useBackup); setError(""); }}>
                {useBackup ? "← Pakai kode authenticator" : "Kehilangan perangkat? Pakai backup code"}
              </button>
            </form>
          ) : (
          <form className="mt-8 space-y-4" onSubmit={handleLogin}>
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  data-testid="login-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9"
                  placeholder="email@myclinic.id"
                  required
                />
              </div>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full"
            >
              {loading ? "Memproses..." : "Masuk"}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}
